import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "../../../a-atoms/icons/Icon";
import style from "./Select.module.css";
import Checkbox from "../checkbox/Checkbox";

const Select = (props) => {
  const [show, setShow] = useState(false);
  const itemRef = useRef();
  const containerRef = useRef();
  const optionsPortalRef = useRef();
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0, width: 0, flipUp: false });

  // Calculate position synchronously before showing dropdown
  const calculatePosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const estimatedDropdownHeight = Math.min(300, (props.options?.length || 0) * 40 + 32); // Rough estimate
      
      // Check if dropdown would go off-screen
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldFlipUp = spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow;
      
      // Use viewport coordinates for fixed positioning
      return {
        top: shouldFlipUp ? rect.top - estimatedDropdownHeight : rect.bottom,
        left: rect.left,
        width: rect.width,
        flipUp: shouldFlipUp
      };
    }
    return { top: 0, left: 0, width: 0, flipUp: false };
  };

  // Handle outside click detection including portal element
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        show &&
        itemRef.current &&
        !itemRef.current.contains(event.target) &&
        optionsPortalRef.current &&
        !optionsPortalRef.current.contains(event.target)
      ) {
        setShow(false);
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [show]);

  // Update position on scroll and resize when dropdown is open
  useEffect(() => {
    if (show && containerRef.current) {
      const updatePosition = () => {
        const position = calculatePosition();
        setPortalPosition(position);
      };

      // Update position on scroll and resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [show, props.options]);

  function showBooleanHandler() {
    const newShowState = !show;
    // Calculate position before setting show to true to prevent animation from top-left
    if (newShowState) {
      const position = calculatePosition();
      setPortalPosition(position);
    }
    setShow(newShowState);
  }

  function onChangeHandler(e, index) {
    props.onChange(e, index);
    // Close dropdown after selection in single-select mode
    if (!props.multiselect) {
      setShow(false);
    }
  }

  const [value, setValue] = useState("");
  useEffect(() => {
    if (props.multiselect) {
      if (
        props.selectedOptions &&
        props.selectedOptions.length === props.options.length
      ) {
        setValue("Showing all");
      } else {
        if (props.selectedOptions) {
          if (props.selectedOptions.length === 1) {
            const firstOption = props.selectedOptions[0];
            setValue(typeof firstOption === 'object' ? firstOption.value : firstOption);
          } else {
            setValue(props.selectedOptions.length + " applied");
          }
        } else {
          console.log("no data found", props);
        }
      }
    } else {
      if (props.selectedOptions && props.selectedOptions.length > 0) {
        const firstOption = props.selectedOptions[0];
        setValue(typeof firstOption === 'object' ? firstOption.value : firstOption);
      } else {
        setValue("");
      }
    }
  }, [props.selectedOptions]);

  function onSelecteOnly(e, index) {
    props.onSelecteOnly(e, index);
  }

  const optionsContent = (
    <div 
      ref={optionsPortalRef}
      className={`${style.options} ${style.optionsPortal} ${show && style.show} ${portalPosition.flipUp ? style.flipUp : ''}`}
      style={{
        top: `${portalPosition.top}px`,
        left: `${portalPosition.left}px`,
        width: `${portalPosition.width}px`
      }}
    >
      <ul>
        {props.options && props.options.map((option, index) => (
          <li key={index} onClick={!props.multiselect ? (e) => onChangeHandler(e, index) : null}>
            {props.multiselect ? (
              <Checkbox
                checked={option.selected}
                onChange={(e) => onChangeHandler(e, index)}
                value={option.value}
              />
            ) : (
              <span>{option.value}</span>
            )}
            <span
              className={style.only}
              onClick={(e) => onSelecteOnly(e, index)}
            >
              {props.multiselect ? "Only" : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <>
      <div
        ref={itemRef}
        className={`${style.outerContainer} ${
          props.variants ? props.variants.map((i) => style[i]).join(" ") : null
        } ${props.error && style.error} ${props.icon && style.hasLeadingIcon} ${
          show && style.show
        } ${!props.label && style.noLabel} `}
      >
        <div ref={containerRef} className={style.container} onClick={showBooleanHandler}>
        {props.icon ? (
          <span className={style.icon}>
            <Icon>{props.icon}</Icon>
          </span>
        ) : null}
        {/* <input
          ref={inputRef}
          type={type}
          value={value}
          placeholder={props.placeholder}
          onChange={props.onChange}
        /> */}
        <span className={`${style.selectedItems} ${value && style.hasValue}`}>
          {value}
        </span>
        {props.label ? <label>{props.label}</label> : null}
        {/* <label>{props.label}</label> */}
        {props.type === "dropdown" ? (
          <span className={style.iconEnd}>
            <Icon>keyboard_arrow_down</Icon>
          </span>
        ) : null}
        {/* Keep original options div for backward compatibility when not using portal */}
        {!show && (
          <div className={style.options}>
            <ul>
              {props.options && props.options.map((option, index) => (
                <li key={index} onClick={!props.multiselect ? (e) => onChangeHandler(e, index) : null}>
                  {props.multiselect ? (
                    <Checkbox
                      checked={option.selected}
                      onChange={(e) => onChangeHandler(e, index)}
                      value={option.value}
                    />
                  ) : (
                    <span>{option.value}</span>
                  )}
                  <span
                    className={style.only}
                    onClick={(e) => onSelecteOnly(e, index)}
                  >
                    {props.multiselect ? "Only" : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className={style.helper}>
        {props.error ? props.error : props.helper}
      </div>
    </div>
    {show && createPortal(optionsContent, document.body)}
    </>
  );
};

export default Select;
